import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { get } from '../services/apiService';
import { getFullUrl } from '../config/apiConfig';
import { 
  BookOpen, 
  Clock, 
  CheckCircle, 
  TrendingUp, 
  Award, 
  Target,
  Play,
  BarChart3,
  Users,
  Loader
} from 'lucide-react';
import logger from '../utils/logger';

const Dashboard = () => {
  const { currentUser } = useAuth();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [examResults, setExamResults] = useState({});
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [completedExams, setCompletedExams] = useState([]);

  // Fetch exams from API
  useEffect(() => {
    const fetchExams = async () => {
      try {
        setLoading(true); 
        const response = await get(getFullUrl('exams')); 
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Parse the response body
        let actualData = data;
        if (data && data.body) {
          actualData = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
        }
        
        // Handle the new backend structure where examSets is the main array
        const examsArray = [];
        
        if (actualData && actualData.examSets && Array.isArray(actualData.examSets)) {
          actualData.examSets.forEach(set => {
            // Each set object is now an exam directly (not nested under set.exams)
            const examObj = {
              id: set.examId,
              title: set.type || 'Exam',
              description: `Grade ${set.PK ? set.PK.split('#')[1] : 'Unknown'} - ${set.title}`,
              category: set.type || 'General',
              difficulty: 'Medium', // Default difficulty since it's not in the new structure
              duration: set.duration || 40,
              questions: set.questionCount, // Questions count not available in new structure
              tags: set.tags || [],
              grade: set.PK ? set.PK.split('#')[1] : 'Unknown',
              package: set.PK ? set.PK.split('#')[3] : 'PKG_STANDARD',
              setNumber: set.setNumber,
              setInfo: set.title,
              purchasedPackage: set.PK ? set.PK.split('#')[3] : 'PKG_STANDARD',
              isCompleted: set.isCompleted || false
            };
            examsArray.push(examObj);
          });
        }
        
        logger.log('Parsed exams array for dashboard:', examsArray);
        setExams(examsArray);
        
        // Extract completed exams from API response
        if (actualData && actualData.completedExams && Array.isArray(actualData.completedExams)) {
          setCompletedExams(actualData.completedExams);
          logger.log('✅ Completed Exams:', actualData.completedExams);
          
          // Populate examResults from completed exams for recent results display
          const resultsMap = {};
          actualData.completedExams.forEach(exam => {
            if (exam.examId && exam.percentage !== undefined) {
              resultsMap[exam.examId] = {
                examTitle: exam.examTitle || 'Exam',
                score: exam.percentage || 0,
                grade: exam.grade || 'Unknown',
                date: exam.completedAt || new Date().toISOString(),
                percentage: exam.percentage || 0
              };
            }
          });
          
          // Ensure examResults is set before UI renders
          setExamResults(resultsMap);
          
          // Debug after state update
          setTimeout(() => {
            logger.log('✅ Populated Exam Results:', resultsMap);
            logger.log('✅ Results Map Keys:', Object.keys(resultsMap));
            logger.log('✅ Results Map Values:', Object.values(resultsMap));
            logger.log('🔍 Final Exam Results State:', resultsMap);
          }, 0);
        } else {
          setCompletedExams([]);
          setExamResults({});
        }
        
      } catch (err) {
        logger.error('Error fetching exams for dashboard:', err);
        setError('Failed to load exams. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    // Fetch purchase history to get purchased grades
    const fetchPurchaseHistory = async () => {
      try {
        const response = await get(getFullUrl('purchases'));
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        logger.log('Fetched purchase history for dashboard:', data);
        
        // Parse response body
        let actualData = data;
        if (data && data.body) {
          actualData = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
        }
        
        let purchases = [];
        if (Array.isArray(actualData)) {
          purchases = actualData;
        } else if (actualData && actualData.purchases && Array.isArray(actualData.purchases)) {
          purchases = actualData.purchases;
        }
        
        setPurchaseHistory(purchases);
        
      } catch (err) {
        logger.error('Error fetching purchase history for dashboard:', err);
      }
    };

    fetchExams();
    fetchPurchaseHistory();
  }, []);

  // Calculate stats based on fetched exams and purchase history
  const totalExams = exams.length;
  const purchasedGrades = [...new Set(purchaseHistory.map(purchase => purchase.grade).filter(grade => grade && grade !== 'Unknown'))];
  const availableGrades = [...new Set(exams.map(exam => exam.grade).filter(grade => grade !== 'Unknown'))];
  const uniqueGrades = [...new Set([...purchasedGrades, ...availableGrades])];
  const recentResults = completedExams.slice(-10);
  
  // Debug: Check completedExams usage
  
  // Calculate stats from completedExams
  const totalExamsTaken = completedExams.length;
  const averageScore = totalExamsTaken > 0 
    ? Math.round(completedExams.reduce((acc, exam) => acc + (exam.percentage || 0), 0) / totalExamsTaken)
    : 0;
  
  // Grades from completed exams for breakdown display
  const completedGradesSorted = [...new Set(completedExams.map(exam => exam.grade).filter(Boolean))].sort((a, b) => a - b);

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getGradeColor = (grade) => {
    const colorMap = {
      '3': 'bg-blue-100 text-blue-800',
      '5': 'bg-green-100 text-green-800',
      '7': 'bg-yellow-100 text-yellow-800',
      '9': 'bg-red-100 text-red-800'
    };
    return colorMap[grade] || 'bg-gray-100 text-gray-800';
  };

  // Show first 3 exams for dashboard
  const dashboardExams = exams.slice(0, 3);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          Welcome back, {currentUser?.name}!
        </h1>
        <p className="mt-2 text-gray-600">
          Ready to practice and improve your skills?
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-primary-100 rounded-lg p-3">
              <BookOpen className="h-6 w-6 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Exams</p>
              <p className="text-2xl font-semibold text-gray-900">{totalExams}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-green-100 rounded-lg p-3">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Completed</p>
              <p className="text-2xl font-semibold text-gray-900">{totalExamsTaken}</p>
              {completedExams.length > 0 && completedGradesSorted.length > 0 && (
                <div className="grid grid-cols-2 gap-x-2 gap-y-1 mt-1">
                  {completedGradesSorted.map(g => {
                    const count = completedExams.filter(e => e.grade === g).length;
                    if (count === 0) return null;
                    return (
                      <div key={g} className="text-xs text-gray-500">
                        Grade {g}: <span className="font-medium">{count}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-yellow-100 rounded-lg p-3">
              <TrendingUp className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Average Score</p>
              <p className="text-2xl font-semibold text-gray-900">{averageScore}%</p>
              {completedExams.length > 0 && completedGradesSorted.length > 0 && (
                <div className="grid grid-cols-2 gap-x-2 gap-y-1 mt-1">
                  {completedGradesSorted.map(g => {
                    const gradeExams = completedExams.filter(e => e.grade === g);
                    if (gradeExams.length === 0) return null;
                    const avg = Math.round(gradeExams.reduce((acc, e) => acc + (e.percentage || 0), 0) / gradeExams.length);
                    return (
                      <div key={g} className="text-xs text-gray-500">
                        Grade {g}: <span className="font-medium">{avg}%</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-accent-100 rounded-lg p-3">
              <Award className="h-6 w-6 text-accent-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Grade Levels</p>
              <div className="flex items-center mt-1">
                {uniqueGrades.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {uniqueGrades.slice(0, 2).map((grade, index) => (
                      <span key={index} className={`text-xs px-2 py-1 rounded-full ${getGradeColor(grade)}`}>
                        Grade {grade}
                      </span>
                    ))}
                    {uniqueGrades.length > 2 && (
                      <span className="text-xs text-gray-500">+{uniqueGrades.length - 2} more</span>
                    )}
                  </div>
                ) : (
                  <p className="text-2xl font-semibold text-gray-900">-</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Grade Cards - Show if more than one grade */}
      {uniqueGrades.length > 1 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Available Grade Levels</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {uniqueGrades.map((grade) => (
              <div key={grade} className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">Grade {grade}</h3>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getGradeColor(grade)}`}>
                    {exams.filter(exam => exam.grade === grade).length} exams
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  Practice tests for Grade {grade} students
                </div>
                <Link 
                  to={`/exams?grade=${grade}`}
                  className="mt-4 inline-flex items-center text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  View All
                  <Play className="h-4 w-4 ml-1" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Available Exams */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Available Exams</h2>
              <Link to="/exams" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                View All
              </Link>
            </div>
          </div>
          <div className="p-6">
            {loading ? (
              <div className="text-center py-8">
                <Loader className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-2" />
                <p className="text-gray-600">Loading exams...</p>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-600">{error}</p>
              </div>
            ) : dashboardExams.length > 0 ? (
              <div className="space-y-4">
                {dashboardExams.map((exam) => (
                  <div key={exam.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-medium text-gray-900">{exam.title}</h3>
                        <span className={`text-xs px-2 py-1 rounded-full ${getGradeColor(exam.grade)}`}>
                          Grade {exam.grade}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{exam.description}</p>
                      <div className="flex items-center space-x-4">
                        <span className="text-sm text-gray-500 flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          {exam.duration}min
                        </span>
                        <span className="text-sm text-gray-500">{exam.questions} questions</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          exam.difficulty === 'Easy' ? 'bg-green-100 text-green-800' :
                          exam.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {exam.difficulty}
                        </span>
                      </div>
                    </div>
                    <Link
                      to="/exams"
                      className="btn-primary flex items-center space-x-1"
                    >
                      <BookOpen className="h-4 w-4" />
                      <span>View Exams</span>
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No exams available</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Results */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Recent Results</h2>
              <BarChart3 className="h-5 w-5 text-gray-400" />
            </div>
          </div>
          <div className="p-6">
            {recentResults.length > 0 ? (
              <div className="space-y-4">
                {recentResults.map((result, index) => (
                  <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{result.examTitle}</h3>
                      <div className="flex items-center space-x-4 mt-1">
                        <span className="text-sm text-gray-500">
                          Grade {result.grade}
                        </span>
                        <span className="text-sm text-gray-500">
                          Set {result.set}
                        </span>
                        <span className="text-sm text-gray-500">
                          {result.type}
                        </span>
                        <span className="text-sm text-gray-500">
                          {result.package}
                        </span>
                        <span className="text-sm text-gray-500">
                          {result.completedAt ? new Date(result.completedAt).toLocaleDateString() : 'Recent'}
                        </span>
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${getScoreColor(result.score)}`}>
                      {result.score}%
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No exam results yet</p>
                <p className="text-sm text-gray-400 mt-1">Start practicing to see your results here</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
